import {
  Recipe,
  RecipeID,
  IFactoryRecipe,
  RecipeRecord,
} from '../models/recipe.model';
import { ProductID, ProductRecord } from '../models/product.model';
import { IDataTable } from '../models/data-table.model';
import {
  getProductByIdDeprecated,
  getProductIdsInElement,
} from './product.helpers';
import {
  ElementKind,
  Baseline,
  IElement,
  OneOfChildElements,
  OneOfElementListChildren,
  OneOfElements,
  OneOfParentElements,
  Project,
} from '../models/project.model';
import {
  flattenElements,
  getAllBuildingVersions,
  getAllProductElements,
  getChildElements,
  isElement,
  isProductElement,
} from './recursive-element.helpers';
import {
  ProjectChange,
  ProjectChangeType,
} from './pipeline/update-engine.models';
import { collectProposalChangesInOrder } from './proposals/proposals.helpers';
import { toLookup } from './utils.helpers';
import { Results, SbefGroupResults } from '../models/unit.model';
import { autoUpdateElementQuantity } from './auto-quantity.helpers';
import type { ExpressionVariablesRecord } from './expression-variables.helpers';
import { createElementOfType, createElements } from './element-factory.helpers';
import {
  ElementPropertyName,
  ElementPropertySource,
  ElementPropertySourceID,
  IElementProperty,
  IFactoryProperty,
  PropertyResolvedCountRecord,
} from '../models/element-property.model';
import { isEqualElements, isGenerated } from './element.helpers';
import { cloneDeep, isObject, uniq } from 'lodash';
import { generateElementConversionFactorSum } from './results.helpers';
import {
  applyElementPropertiesOfSource,
  getElementAndQuantityProperties,
  getElementProperties,
  getElementPropertiesNotOfSource,
  getElementPropertySource,
  getElementPropertySourceId,
  getElementSourceId,
  hasFallbackCount,
  isElementPropertyListEqual,
  isElementPropertyOfSource,
} from './element-property.helpers';
import { validateElementProperties } from '../validation/element-property.validation';
import { getAvailableCategories } from '../templates/categories';
import {
  getCategoryPropertyValueRecord,
  getElementCategory,
} from './element-category.helpers';
import {
  filterObject,
  getId,
  hasDefinedProperties,
  isPrimitive,
  mapFilterRecord,
  omit,
  omitUndefined,
  replaceProperties,
} from './object.helpers';
import { createElementProperties } from './element-property-factory.helpers';
import {
  createElementQuantityRecord,
  getElementQuantityResolvedCountByName,
  getSelectedQuantityName,
  isElementQuantityExpressionName,
  isElementQuantityExpressionProperty,
} from './element-quantity.helpers';
import { getElementQuantityProperties } from './element-quantity-core.helpers';
import { ElementCategoryID } from '../models/element-categories.model';
import { getTimestamps } from './date.helpers';
import shallowEqual, { EMPTY_FROZEN_ARRAY } from './array.helpers';
import { getDefaultFallbackExpression } from './auto-quantity.helpers';
import { createExpression } from './expression-factory.helpers';
import {
  enrichElementStructure,
  updateBuildingVersionProperties,
  updateElements,
} from './project.helpers';
import { cacheFactory, required } from './function.helpers';
import { createId } from './id.helpers';
import { sumConversionFactors } from './conversion-factors.helpers';
import { multiplyConversionFactors } from './conversion.helpers';
import { guards } from './typeguards.helpers';
import { getFlatResults } from './sbef-results.helpers';
import { syncPiecesQuantityAfterRecipeApply } from './pieces-quantity.helpers';
import { OneOfFactoryElements } from '../models/factory-element.model';
import {
  isCommonElementCategoryProperty,
  isSystemElementCategoryPropertyName,
} from '@shared/templates/categories/common-element-category-properties.helpers';

const AUTO_RECIPE_ID_PREFIX = 'auto:';

const REMOVED_CATEGORY_PROPERTY_NAMES = new Set([
  'weather_resistance',
  'thermal_insulation',
  'load_bearing',
  'sound_insulation',
]);

/**
 * Test if element and recipe are equal (to show modified asterix etc)
 * @param recipe
 * @param element
 * @param ignoreCategoryProperties If comparison should ignore category properties
 * @returns
 */
export const isRecipeAndElementEqual = (
  recipe: Recipe,
  element: IElement,
  dataTables: IDataTable[],
): boolean => {
  // Create an element based on the recipe to compare with (more stable than comparing the raw recipe, but expesive so do last)
  const recipeElement = cacheFactory(
    () =>
      enrichElementStructure(
        applyRecipe(createElementOfType(ElementKind.Element), recipe),
        [],
        {},
        {},
        dataTables,
      ),
    `isRecipeAndElementEqual[${recipe.id}]`,
    [recipe, dataTables],
  );

  if (isRecipeCategoryPropertyValueRecordModified(element, recipe)) {
    return false;
  }

  const childIngredients = getChildElements(recipeElement).filter(
    (c) => !isGenerated(c),
  );
  const childElements = getChildElements(element).filter(
    (c) => !isGenerated(c),
  );

  // If length of children differs, elements are not equal
  if (childIngredients.length !== childElements.length) {
    return false;
  }

  const recipeProperties = getElementPropertiesNotOfSource(
    recipe,
    ElementPropertySource.Category,
  );

  const elementProperties = getElementPropertiesNotOfSource(
    element,
    ElementPropertySource.Category,
  ).filter((p) => !isCommonElementCategoryProperty(p));

  if (!isElementPropertyListEqual(recipeProperties, elementProperties)) {
    return false;
  }

  return recipeElement.elements.every((ingredient, index) => {
    const childElement = childElements[index];
    return childElement ? isEqualElements(childElement, ingredient) : false;
  });
};

/**
 * Test if the category property value record of an element is modified compared to a recipe.
 * IE. That a category property value has been changed
 * @param element
 * @param recipe
 * @returns
 */
export const isRecipeCategoryPropertyValueRecordModified = (
  element: IElement,
  recipe: Recipe,
): boolean => {
  const filter = (
    value: number | string | boolean | undefined,
    key: string,
  ) => {
    if (
      !value ||
      value === 'none' ||
      isSystemElementCategoryPropertyName(key) ||
      REMOVED_CATEGORY_PROPERTY_NAMES.has(key)
    ) {
      return undefined;
    }
    return !!value;
  };

  const elementRecord = filterObject(
    getCategoryPropertyValueRecord(element),
    filter,
  );
  const recipeRecord = filterObject(
    recipe.category_property_value_record,
    filter,
  );

  return !shallowEqual(elementRecord, recipeRecord);
};

/**
 * Element totals divided by the element's current quantity.
 *
 * The quantity is the resolved count of the selected quantity. When none is
 * selected, the category default is used (a slab's m² is `area_top`).
 * Undefined when that count is missing or not positive, or when
 * {@link elementResults} has no numeric values. SBEF group buckets are omitted
 * so the shape matches migration-written {@link Recipe.results}.
 */
export const elementResultsPerQuantity = (
  element: IElement,
  elementResults: SbefGroupResults | undefined,
): Results | undefined => {
  const quantityName = getSelectedQuantityName(element);
  if (!quantityName || !isElementQuantityExpressionName(quantityName)) {
    return undefined;
  }

  const quantity = getElementQuantityResolvedCountByName(
    element,
    quantityName,
    0,
  );
  if (!guards.is.finiteNumber(quantity) || !(quantity > 0)) return undefined;

  const perQuantity = multiplyConversionFactors(
    getFlatResults(elementResults ?? {}),
    1 / quantity,
  );
  return Object.keys(perQuantity).length > 0 ? perQuantity : undefined;
};

/**
 * Create a new recipe from an element.
 *
 * {@link elementResults} are stored as per-quantity totals via
 * {@link elementResultsPerQuantity}. Omit them, or pass an element whose
 * quantity does not resolve, and `results` stays unset.
 */
export const createRecipeFromElement = (
  owner: string | undefined,
  element: IElement,
  elementResults: Results = {},
): Recipe => {
  const category_property_value_record =
    getCategoryPropertyValueRecord(element);

  const results = elementResultsPerQuantity(element, elementResults);

  return createRecipe({
    name: element.name ?? element.fallbackName,
    category_id: element.category_id,
    category_property_value_record,
    owner,
    organizations: [],
    results,
    elements: getRecipeElementsFromElement(element),
    properties: element.properties,
    ...getTimestamps(),
  });
};

export const createRecipe = (defaults: IFactoryRecipe = {}): Recipe => {
  const properties: IFactoryProperty[] = (defaults.properties ?? [])
    // Don't include category properties
    .filter(
      (p) => !isElementPropertyOfSource(p, ElementPropertySource.Category),
    )
    // Remove recipe id. These should be applied when recipe is applied instead
    .map((p) => omit(p, ElementPropertySource.Recipe) as IFactoryProperty);

  return {
    name: '',
    description: '',
    category_id: ElementCategoryID.None,
    category_property_value_record: {},
    organizations: [],
    ...omitUndefined(defaults),
    elements: createElements(defaults.elements),
    id: '',
    properties: createElementProperties(properties),
    ...getTimestamps(),
  };
};

/**
 * Replace a recipe's composition with {@link element}.
 *
 * `results` becomes {@link elementResultsPerQuantity}. When that is undefined
 * the recipe keeps the results it already has.
 */
export const updateRecipeFromElement = (
  recipe: Recipe,
  element: IElement,
  elementResults: Results = {},
): Recipe => {
  const newRecipe = createRecipeFromElement(
    recipe.owner,
    element,
    elementResults,
  );

  const updated: Recipe = {
    ...recipe,
    ...omitUndefined(
      omit(
        newRecipe,
        'id',
        'created_at',
        'organizations',
        'description',
        'category_id',
        'name',
      ),
    ),
  };
  return updated;
};

/*
TODO: Since recipe.ingredients has been changed to recipe.elements, 
these helpers might not be needed any longer? 
*/
const getRecipeElementsFromElement = (
  element: IElement,
): OneOfChildElements[] => elementsToIngredients(getChildElements(element));

/**
 * Drop a recipe source the element-property validator would reject.
 *
 * The property's source id must equal the element's resolved recipe id
 * ({@link getElementSourceId}). The value is kept; the `recipe_id` key is removed.
 *
 * @param property - Property that may still point at a recipe
 * @param elementRecipeId - Resolved {@link IElement.recipe_id}, if any
 */
const withoutMismatchedRecipeSource = (
  property: IElementProperty,
  elementRecipeId: ElementPropertySourceID | undefined,
): IElementProperty => {
  if (getElementPropertySource(property) !== ElementPropertySource.Recipe) {
    return property;
  }
  if (getElementPropertySourceId(property) === elementRecipeId) return property;
  // {@link getRecipeId} uses `in`, so a key whose value is `undefined` still counts as present.
  // `omit` loses the property-type discriminant, so drop the key on a copy instead.
  const next: IElementProperty = { ...property };
  delete next.recipe_id;
  return next;
};

/**
 * Clear recipe sources on this element and its descendants that do not match
 * each element's own recipe id.
 *
 * Applying a catalog ingredient copies it onto the project without giving the
 * child the parent recipe id. A property saved with `recipe_id` set and no
 * element `recipe_id` then fails validation.
 *
 * @param element - Element tree to normalize
 */
const clearMismatchedRecipePropertySources = (element: IElement): IElement => {
  const elementRecipeId = getElementSourceId(
    element,
    ElementPropertySource.Recipe,
  );
  let changed = false;

  const properties = getElementProperties(element).map((property) => {
    const next = withoutMismatchedRecipeSource(property, elementRecipeId);
    if (next !== property) changed = true;
    return next;
  });

  const elements = element.elements.map((child) => {
    if (!isElement(child)) return child;
    const next = clearMismatchedRecipePropertySources(child);
    if (next !== child) changed = true;
    return next;
  });

  if (!changed) return element;
  return { ...element, properties, elements };
};

/**
 * Same-reference walk of a child list. Returns the input array when every child is unchanged.
 *
 * @param elements - Direct children to normalize
 */
const clearMismatchedRecipePropertySourcesInChildren = (
  elements: OneOfChildElements[],
): OneOfChildElements[] => {
  let changed = false;
  const next = elements.map((child) => {
    if (!isElement(child)) return child;
    const cleared = clearMismatchedRecipePropertySources(child);
    if (cleared !== child) changed = true;
    return cleared;
  });
  return changed ? next : elements;
};

/**
 * Drop recipe property sources already stored on a project that validation would reject.
 *
 * Save-as and apply heal new writes. A baseline saved before that still has the
 * ingredient value with a recipe source and no element `recipe_id`, and enrich
 * validates that tree. Values stay. Returns {@link project} when nothing mismatches.
 *
 * @param project - Project whose baselines may carry the stale source
 */
export const clearMismatchedRecipePropertySourcesInProject = (
  project: Project,
): Project => {
  const patches: { id: Baseline['id']; elements: OneOfChildElements[] }[] = [];
  for (const version of getAllBuildingVersions(project)) {
    const elements = clearMismatchedRecipePropertySourcesInChildren(
      version.elements,
    );
    if (elements === version.elements) continue;
    patches.push({ id: version.id, elements });
  }
  if (patches.length === 0) return project;
  return updateBuildingVersionProperties(project, ...patches);
};

/**
 * Removes live recipe instance addresses and property ids from a catalog element tree.
 *
 * Live `:r:` addresses are replaced with fresh ids (not deleted). `createRecipe` regenerates
 * them again, but leaving `id: undefined` here poisons the id-keyed {@link flattenElements}
 * memo cache (e.g. via the flatten in {@link elementsToIngredients}), surfacing phantom
 * `undefined`-id elements later in {@link isElementAndPropertyIdsUnique} (Duplicate ids in project).
 */
const stripCatalogElementInstanceRefs = (element: IElement): IElement => {
  const stripped = cloneDeep(element);
  if (isRecipeScopedElementId(stripped.id)) {
    stripped.id = createId({}, true);
  }
  // Keep the element's recipe_id - library recipes should be preserved (DEV-3984).
  // Properties have their recipe source removed (converted to unsourced) since they
  // are now part of the parent recipe's structure, not the child recipe's.
  stripped.properties = createElementProperties(
    getElementProperties(stripped).map((property) => {
      // Catalog ingredients are not recipe instances. Keep the value, drop the source.
      const sourced = withoutMismatchedRecipeSource(property, undefined);
      const { id: _id, ...rest } = sourced;
      return rest as IFactoryProperty;
    }),
    true,
  );
  stripped.quantity = createElementQuantityRecord(
    getElementQuantityProperties(stripped).map(({ id: _id, ...rest }) => rest),
    true,
  );
  stripped.elements = stripped.elements.map(
    (child): OneOfElementListChildren => {
      if (isElement(child)) return stripCatalogElementInstanceRefs(child);
      if (isRecipeScopedElementId(child.id)) {
        return { ...child, id: createId({}, true) };
      }
      return child;
    },
  );
  return stripped;
};

const elementsToIngredients = (
  elements: OneOfChildElements[] = [],
): OneOfChildElements[] => {
  // Make a clone to not affect original element and exclude generated elements
  const clones: OneOfElements[] = cloneDeep(elements)
    .filter((e) => !isGenerated(e))
    .map((child) =>
      isElement(child) ? stripCatalogElementInstanceRefs(child) : child,
    );
  flattenElements(...clones).forEach((element) => {
    if (!isElement(element)) return;
    getElementAndQuantityProperties(element).forEach((p) => {
      // Reset any auto values on quantity properties of type "expression"
      if (isElementQuantityExpressionProperty(p) && hasFallbackCount(p)) {
        p.fallbackCount = createExpression(getDefaultFallbackExpression(p));
      }
    });
  });

  return clones as OneOfChildElements[];
};

export const isElementCategoryMatch = (
  recipe: Recipe,
  elementCategoryId?: ElementCategoryID,
): boolean => {
  if (recipe?.category_id && elementCategoryId) {
    return recipe.category_id === elementCategoryId;
  }
  return false;
};

export const sortRecipesByCO2eTotalRecord = (
  recipes: Recipe[],
  co2eRecord: Record<RecipeID, number>,
): Recipe[] =>
  [...recipes].sort(
    (recipeA, recipeB) =>
      (co2eRecord[recipeA.id] || Number.MAX_VALUE) -
      (co2eRecord[recipeB.id] || Number.MAX_VALUE),
  );

/**
 * Make an element use a recipe. Can be used to reset an element to a recipe.
 * @param recipe
 * @param element
 * @param options - When {@link ApplyRecipeOptions.variablesRecord} is set, recipe children get auto-quantity inheritance from the parent path.
 * @returns
 */
export type ApplyRecipeOptions = {
  parentPath?: OneOfParentElements[];
  variablesRecord?: ExpressionVariablesRecord;
};

/**
 * Regenerate property and quantity ids on an element subtree without changing element ids.
 * Recipe catalog templates may carry ids shared across instances; each apply must be unique
 * project-wide ({@link isElementAndPropertyIdsUnique}).
 */
const regenerateElementInstancePropertyIds = (element: IElement): IElement => {
  const properties = createElementProperties(
    getElementProperties(element).map((property) => {
      const { id: _id, ...rest } = property;
      return rest as IFactoryProperty;
    }),
    true,
  );

  const quantity = createElementQuantityRecord(
    getElementQuantityProperties(element).map((property) => {
      const { id: _id, ...rest } = property;
      return rest;
    }),
    true,
  );

  const elements = element.elements.map((child) =>
    isElement(child) ? regenerateElementInstancePropertyIds(child) : child,
  );

  return { ...element, properties, quantity, elements };
};

export const applyRecipe = (
  element: IElement,
  recipe: Recipe,
  options: ApplyRecipeOptions = {},
): IElement => {
  const recipe_id = recipe.id;
  if (!recipe_id) {
    throw new Error('Recipe id is required to update element from recipe');
  }

  // Stamp deterministic element ids on recipe children so proposal-scoped
  // {@link ProjectChangeType.ElementRecipeApply} edits can address them stably
  // (see {@link buildRecipeChildId}). Property/quantity ids are regenerated per instance so
  // catalog templates cannot collide across elements ({@link regenerateElementInstancePropertyIds}).
  const parentPath = options.parentPath ?? [element];
  const variablesRecord = options.variablesRecord ?? {};
  const elements = createElements(
    assignDeterministicRecipeIds(element.id, recipe_id, recipe.elements),
    false,
  ).map((child) => {
    if (!isElement(child)) return child;
    const withIds = ensureDeterministicRecipeSubtree(child);
    const withQuantities = autoUpdateElementQuantity(
      withIds,
      parentPath,
      variablesRecord,
    );
    // A saved ingredient can carry recipe_id with no element recipe. Drop that source.
    return clearMismatchedRecipePropertySources(
      regenerateElementInstancePropertyIds(withQuantities),
    );
  });

  const applied = applyElementPropertiesOfSource(
    { ...element, elements, recipe_id },
    ElementPropertySource.Recipe,
    getElementProperties(recipe).map((p) => ({
      ...p,
      [ElementPropertySource.Recipe]: undefined,
    })),
  );
  // Fold a recipe `pieces` property into quantity.pieces on first apply only.
  return syncPiecesQuantityAfterRecipeApply(element, applied);
};

/**
 * Deterministic id for a recipe-applied subtree.
 *
 * Top-level recipe children carry `${parentElementId}:r:${recipeId}:${slot}:${index}`
 * so the recipe id is part of the address (used by {@link buildRecipeChildId}).
 * Nested grandchildren extend their parent's id with `${parentChildId}:${slot}:${index}`
 * (used by {@link buildNestedChildId}) — no need to repeat the recipe id, the prefix already encodes it.
 *
 * `slot` is the child element's {@link IElement.category_id} when available so swapping one ingredient
 * in the recipe does not shift sibling ids. Product children fall back to `'p'`.
 */
export const assignDeterministicRecipeIds = (
  parentElementId: string,
  recipeId: RecipeID,
  children: OneOfChildElements[],
): OneOfChildElements[] =>
  children.map((child, index) =>
    stampDeterministicRecipeChild(
      child,
      buildRecipeChildId(parentElementId, recipeId, child, index),
    ),
  );

/**
 * Re-stamps `:r:` child ids on a cloned element tree after {@link createBuildingVersion}
 * regenerated parent uuids but left recipe children with random plain ids.
 */
export const restampDeterministicRecipeChildIdsInTree = (
  elements: OneOfChildElements[],
): OneOfChildElements[] =>
  elements.map((child) => {
    if (!isElement(child)) return child;

    let next: IElement = child;
    if (child.recipe_id && child.elements.length > 0) {
      const restampedChildren = assignDeterministicRecipeIds(
        child.id,
        child.recipe_id,
        child.elements,
      ).map((recipeChild) =>
        isElement(recipeChild)
          ? ensureDeterministicRecipeSubtree(recipeChild)
          : recipeChild,
      );
      next = { ...child, elements: restampedChildren };
    } else if (child.elements.length > 0) {
      next = {
        ...child,
        elements: restampDeterministicRecipeChildIdsInTree(child.elements),
      };
    }

    return next;
  });

const RECIPE_CHILD_ID_INFIX = 'r';

/** True when `elementId` is a recipe-applied child address (contains `:r:`). */
export const isRecipeScopedElementId = (
  elementId: string | undefined,
): boolean => elementId?.includes(`:${RECIPE_CHILD_ID_INFIX}:`) ?? false;

/**
 * True when this element already has a recipe composition to compute from.
 *
 * A `:r:` child or any product descendant counts. Leftover dummy system children
 * (partial project copy, DEV-3656) do not — those hosts still need their recipe
 * children materialized.
 */
export const hostHasRecipeComposition = (element: IElement): boolean => {
  if (element.elements.some((child) => isRecipeScopedElementId(child.id))) {
    return true;
  }
  return getAllProductElements(element).length > 0;
};

/**
 * Baseline element with {@link IElement.recipe_id} whose direct children use legacy plain ids
 * (pre-DEV-3293) rather than deterministic `:r:` addresses.
 */
export const hasLegacyRecipeChildTree = (element: IElement): boolean => {
  if (!element.recipe_id || element.elements.length === 0) return false;
  return !element.elements.some((child) => isRecipeScopedElementId(child.id));
};

/**
 * Parent element id for a recipe-scoped child address
 * (`parent:r:recipeId:slot:index`). Returns undefined when the id is not
 * recipe-scoped or has no parent segment.
 */
export const getRecipeScopedParentElementId = (
  elementId: string,
): string | undefined => {
  if (!isRecipeScopedElementId(elementId)) return undefined;
  const parentId = elementId.split(`:${RECIPE_CHILD_ID_INFIX}:`)[0];
  return parentId || undefined;
};

/**
 * Deterministic id for a direct child under a recipe-scoped element (e.g. generated product slot).
 * @see buildNestedChildId
 */
export const buildDeterministicRecipeNestedChildId = (
  parentChildId: string,
  child: OneOfChildElements,
  index: number,
): string => buildNestedChildId(parentChildId, child, index);

/**
 * Re-stamps any child ids under a recipe-scoped element that still carry random UUIDs
 * (e.g. from a frozen {@link ProjectChangeType.ElementRecipeApply} payload or category dedup).
 */
export const ensureDeterministicRecipeSubtree = (
  element: IElement,
): IElement => {
  let changed = false;
  const elements = element.elements.map((child, index) => {
    let next: OneOfChildElements = child;
    if (isRecipeScopedElementId(element.id)) {
      const expectedId = buildDeterministicRecipeNestedChildId(
        element.id,
        child,
        index,
      );
      if (child.id !== expectedId) {
        next = { ...child, id: expectedId };
        changed = true;
      }
    }
    if (isElement(next)) {
      const nested = ensureDeterministicRecipeSubtree(next);
      if (nested !== next) {
        next = nested;
        changed = true;
      }
    }
    return next;
  });
  return changed ? { ...element, elements } : element;
};

const buildRecipeChildId = (
  parentElementId: string,
  recipeId: RecipeID,
  child: OneOfChildElements,
  index: number,
): string =>
  `${parentElementId}:${RECIPE_CHILD_ID_INFIX}:${recipeId}:${getRecipeChildSlot(child)}:${index}`;

const buildNestedChildId = (
  parentChildId: string,
  child: OneOfChildElements,
  index: number,
): string => `${parentChildId}:${getRecipeChildSlot(child)}:${index}`;

const getRecipeChildSlot = (child: OneOfChildElements): string => {
  if (isElement(child)) return child.category_id ?? ElementCategoryID.None;
  return 'p';
};

const stampDeterministicRecipeChild = (
  child: OneOfChildElements,
  id: string,
): OneOfChildElements => {
  if (!isElement(child)) return { ...child, id };
  const elements = (child.elements ?? []).map((grandchild, i) =>
    stampDeterministicRecipeChild(
      grandchild,
      buildNestedChildId(id, grandchild, i),
    ),
  );
  return { ...child, id, elements };
};

/**
 * Remove references in recipe to products that no longer exists
 * @param products Existing, non-deleted, products (generic, template-products and custom products)
 * @param recipe
 * @returns
 */
export const removeDeletedProductRefsInRecipe = (
  products: ProductRecord,
  recipe: Recipe,
): Recipe => {
  getAllProductElements(recipe).forEach((productElement) => {
    const { product_id, generic_product_id } = productElement;

    if (generic_product_id && !products[product_id]) {
      recipe = updateElements(recipe, {
        id: productElement.id,
        product_id: generic_product_id,
        generic_product_id: undefined,
      });
    }
  });
  return recipe;
};

/**
 * Detach a recipe from an element if it has one.
 * This will leave the recipe properties and child elements intace but remove the connection to the recipe.
 * @param element
 * @returns
 */
export const detachRecipe = (element: IElement): IElement => {
  // Keep old recipe properties but remove their connection to the recipe
  const properties = getElementProperties(element).map((p) =>
    isElementPropertyOfSource(p, ElementPropertySource.Recipe)
      ? { ...p, recipe_id: undefined } // Note that we need to set it do undefined rather than removing it for applyChanges to pick it up
      : p,
  );

  return replaceProperties(element, {
    recipe_id: undefined,
    properties,
  });
};

/**
 * Clear everything related to recipes from an element.
 * @param element
 * @param keepId If we suspect that the reason for clearing is that the recipe is temporary unavailable we can keep the recipe id to be able to restore it later
 * @returns
 */
export const clearRecipe = (element: IElement): IElement => {
  // Remove any properties that are connected to a recipe
  const properties = getElementProperties(element).filter(
    (p) => !isElementPropertyOfSource(p, ElementPropertySource.Recipe),
  );

  // When linked to a recipe, drop all children (legacy plain ids and `:r:`). When
  // unlinked, still strip orphaned `:r:` output (e.g. after detach) so a later apply
  // or clear cannot stack a second recipe subtree (Duplicate ids in project).
  const elements = element.recipe_id
    ? []
    : element.elements.filter((child) => !isRecipeScopedElementId(child.id));

  return replaceProperties(element, {
    recipe_id: undefined,
    properties,
    elements,
  });
};

/**
 * Clears the element of previous recipe and applies the new one.
 * Drops unsaved (element-only) properties and recipe fields not present on {@link recipe}.
 *
 * @param element - Element to update
 * @param recipe - Target recipe
 * @returns Element with new recipe structure and properties
 */
export const overwriteRecipe = (
  element: IElement,
  recipe: Recipe,
): IElement => {
  // Remove any properties that are connected to the element only
  const properties = getElementProperties(element).filter(
    (p) => !isElementPropertyOfSource(p, undefined),
  );

  return applyRecipe(clearRecipe({ ...element, properties }), recipe);
};

/**
 * Proposal-only siblings with plain ids to keep across a recipe swap (DEV-3350).
 *
 * First-time apply and legacy trees without `:r:` children have no user-added siblings —
 * previous-state children are hidden instead of kept active.
 *
 * @param element - Element state before {@link clearRecipe} during a proposal recipe swap
 * @returns Plain-id children to append after {@link applyRecipe}
 */
const collectProposalUserAddedChildren = (
  element: IElement,
): OneOfChildElements[] => {
  const hasRecipeScopedChildren = element.elements.some((child) =>
    isRecipeScopedElementId(child.id),
  );
  if (!hasRecipeScopedChildren) return [];

  return element.elements.filter((child) => !isRecipeScopedElementId(child.id));
};

/**
 * Previous-state children to keep in the proposal-resolved tree as hidden.
 * User-added siblings (DEV-3350) stay as they are; recipe apply must not leave
 * baseline/legacy children contributing CO2e or listed as re-enableable rows.
 *
 * Children whose id the new recipe regenerates are left out: `:r:` addresses are derived from
 * parent + recipe + slot, so re-applying a recipe (or an edited revision of the same recipe id)
 * reuses those ids. Keeping both copies would put duplicate ids in the tree and fail
 * {@link validateProject}; the applied child already owns that slot.
 *
 * @param appliedChildIds - Ids {@link applyRecipe} just produced for the new recipe
 */
const collectPreviousStateChildrenToHide = (
  element: IElement,
  userAddedIds: ReadonlySet<string>,
  appliedChildIds: ReadonlySet<string>,
): IElement[] =>
  element.elements.filter(
    (child): child is IElement =>
      isElement(child) &&
      !userAddedIds.has(child.id) &&
      !appliedChildIds.has(child.id),
  );

/**
 * Hide a replaced previous-state child in the proposal-resolved tree.
 * {@link IElement.isDeactivated} drops it from CO2e; {@link IElement.isDeleted}
 * drops it from the element list (same filter as user deletes) so Enable cannot
 * restore the old recipe next to the new one.
 *
 * Reusing `isDeleted` deliberately makes these children indistinguishable from a proposal
 * soft-delete. That is what we want: they exist only in the resolved tree, every consumer
 * treats the flag as "hide", and promoting the proposal to a baseline strips them through the
 * same path as user deletes so no tree is ever persisted carrying both recipes (DEV-3641).
 */
const hidePreviousStateChild = (child: IElement): IElement => ({
  ...child,
  isDeactivated: true,
  isDeleted: true,
});

/**
 * Proposal-scoped recipe swap: apply {@link recipe} structurally while keeping baseline
 * properties the new recipe does not define (e.g. after swapping to a recipe that omits
 * `balklängd_invändig` but child expressions still reference it on the parent).
 *
 * Previous-state children stay in the tree as deactivated and deleted so they
 * no longer contribute CO2e or appear as disabled list rows (Enable would restore
 * the old recipe). Proposal-added plain-id siblings are kept as-is (DEV-3350).
 *
 * @param element - Baseline element (proposal resolve starts from this tree)
 * @param recipe - Target recipe
 * @param options - Optional {@link ApplyRecipeOptions} for child quantity inheritance
 * @returns Element with new recipe_id/children, hidden previous children, plus merged properties
 */
export const overwriteRecipeForProposal = (
  element: IElement,
  recipe: Recipe,
  options: ApplyRecipeOptions = {},
): IElement => {
  // Proposal-added siblings (e.g. other-product / concrete rows) use plain ids, not `:r:` addresses.
  // `clearRecipe` drops all children when `recipe_id` is set — capture them before the swap (DEV-3350).
  const userAddedChildren = collectProposalUserAddedChildren(element);
  const userAddedIds = new Set(userAddedChildren.map((child) => child.id));
  const applied = applyRecipe(clearRecipe(element), recipe, options);
  const hiddenPreviousChildren = collectPreviousStateChildrenToHide(
    element,
    userAddedIds,
    new Set(applied.elements.map((child) => child.id)),
  ).map(hidePreviousStateChild);
  const extraChildren = [...hiddenPreviousChildren, ...userAddedChildren];
  const withUserChildren =
    extraChildren.length === 0
      ? applied
      : { ...applied, elements: [...applied.elements, ...extraChildren] };
  const appliedNames = new Set(
    getElementProperties(withUserChildren).map((property) => property.name),
  );
  const preserved = baselinePropertiesFromElement(element, appliedNames);
  if (preserved.length === 0) return withUserChildren;

  return {
    ...withUserChildren,
    properties: validateElementProperties([
      ...getElementProperties(withUserChildren),
      ...preserved,
    ]),
  };
};

/**
 * Detach stale recipe linkage on baseline properties the new recipe does not define.
 */
const baselinePropertiesFromElement = (
  element: IElement,
  appliedNames: ReadonlySet<string | undefined>,
): IElementProperty[] =>
  getElementProperties(element)
    .filter((property) => !appliedNames.has(property.name))
    .map((property) => {
      if (!isElementPropertyOfSource(property, ElementPropertySource.Recipe)) {
        return property;
      }
      // Detach stale recipe linkage so validation matches the new element.recipe_id.
      return omit(
        { ...property, recipe_id: undefined },
        ElementPropertySource.Recipe,
      ) as IElementProperty;
    });

/**
 * Deterministic ids {@link applyRecipe} stamps on the direct children of {@link recipe} when it
 * is applied to {@link parentElementId} — see {@link assignDeterministicRecipeIds}.
 */
const getAppliedRecipeChildIds = (
  parentElementId: string,
  recipe: Pick<Recipe, 'id' | 'elements'>,
): string[] =>
  assignDeterministicRecipeIds(parentElementId, recipe.id, recipe.elements).map(
    getId,
  );

/**
 * Children to embed on a proposal {@link ProjectChangeType.ElementRecipeApply} payload: exactly
 * the direct children the apply of {@link recipe} produced, matched on the deterministic
 * `${preview.id}:r:${recipe.id}:${slot}:${index}` addresses it stamps.
 *
 * Matching addresses instead of "recipe-scoped and visible" keeps the payload index-aligned with
 * the preview. Replay re-derives ids from array position, so omitting any child the recipe defines
 * — e.g. one the catalog itself carries hidden after a Save as from a swapped element — would
 * shift every later sibling's id away from the preview.
 *
 * Hidden previous-state children and DEV-3350 user-added siblings never hold an applied address,
 * so they stay out; {@link overwriteRecipeForProposal} re-derives both from the pre-apply element
 * on replay so user-added rows are not stamped as extra `:r:` ingredients (double CO2e).
 */
export const getProposalRecipeApplyPayloadElements = (
  preview: IElement,
  recipe: Pick<Recipe, 'id' | 'elements'>,
): OneOfChildElements[] => {
  const appliedIds = new Set(getAppliedRecipeChildIds(preview.id, recipe));
  return preview.elements.filter((child) => appliedIds.has(child.id));
};

/**
 * Apply a recipe to an element based on an id.
 * Supports the 'none' | 'detach' | 'auto' id's.
 * @param recipes List of recipes sorted by lowest co2e to highest
 * @param element
 * @param id New id. Can be 'none' | 'detach' | 'auto' or a regular recipe id
 * @returns
 */
export const applyRecipeById = (
  recipes: Recipe[],
  products: ProductRecord,
  element: IElement,
  id: RecipeID | undefined,
  options: ApplyRecipeOptions = {},
): IElement => {
  if (id === 'detach') {
    return detachRecipe(element);
  } else if (id === 'none' || !id) {
    return clearRecipe(element);
  }
  const match = required(recipes.find((r) => r.id === id));
  const recipe = removeDeletedProductRefsInRecipe(products, match);
  return applyRecipe({ ...element, recipe_id: id }, recipe, options);
};

/**
 * Get record containing conversion factors of each recipe.
 * Note that these are only per unit of a recipe
 * @param recipes
 * @param products
 * @param version
 * @returns
 */
export const getRecipeConversionFactorRecord = (
  recipes: Recipe[],
  products: ProductRecord,
  version: Baseline,
): Record<RecipeID, Results> => {
  return recipes.reduce(
    (acc, recipe) => ({
      ...acc,
      [recipe.id]: getRecipeConversionFactorTotals(recipe, products, version),
    }),
    {},
  );
};

/**
 * Get ConversionFactors for a single unit of a Recipe.
 * To get CO2e this value can be multiplied with an element count
 * @param recipes
 * @param products
 * @param version
 * @returns
 */
export const getRecipeConversionFactorTotals = (
  recipe: Recipe,
  products: ProductRecord,
  version: Baseline,
): Results => {
  const factors = flattenElements(recipe)
    .filter(isProductElement)
    .map((ingredient) => {
      const product = getProductByIdDeprecated(
        products,
        version,
        ingredient.product_id,
      );
      return generateElementConversionFactorSum(version, ingredient, product);
    });
  return sumConversionFactors(...factors);
};

export const getRecipeIdsInUse = (
  root: Project | Baseline | OneOfElements | Recipe,
  allowAutoRecipes = false,
): RecipeID[] =>
  uniq(
    flattenElements(root)
      .map(getRecipeId)
      .filter((id): id is string => isValidStoreRecipeId(id, allowAutoRecipes)),
  );

/**
 * Get all recipes used in element
 * @param element
 * @param recipes All recipes in the store
 * @returns
 */
export const getRecipesUsedInElement = (
  element: OneOfElements,
  recipes: Recipe[] = [],
): Recipe[] => {
  // Get getRecipeIdsInUse is expensive so we avoid it if possible
  if (recipes.length === 0) {
    return EMPTY_FROZEN_ARRAY as Recipe[];
  }
  const recipeIds = getRecipeIdsInUse(element);
  return recipes.filter(
    (recipe) => isRecipe(recipe) && recipeIds.includes(recipe.id),
  );
};

/**
 * Store-backed {@link RecipeID}s on the element tree and in proposal
 * {@link ProjectChangeType.ElementRecipeApply} actions.
 */
export const getRecipeIdsReferencedByProject = (
  project: Project,
  allowAutoRecipes = true,
): RecipeID[] => {
  const ids = new Set(getRecipeIdsInUse(project, allowAutoRecipes));

  for (const version of getAllBuildingVersions(project)) {
    for (const action of version.actions ?? []) {
      for (const change of action.changes) {
        if (change.type !== ProjectChangeType.ElementRecipeApply) continue;

        if (isValidStoreRecipeId(change.recipeId, allowAutoRecipes)) {
          ids.add(change.recipeId);
        }
        const payloadId = change.recipe?.id;
        if (isValidStoreRecipeId(payloadId, allowAutoRecipes)) {
          ids.add(payloadId);
        }
      }
    }
  }

  return [...ids];
};

/**
 * Updates {@link latest} when {@link change} is a matching {@link ProjectChangeType.ElementRecipeApply}.
 */
const updateLatestEmbeddedRecipeFromChange = (
  change: ProjectChange,
  recipeId: RecipeID,
  latest: Recipe | undefined,
): Recipe | undefined => {
  if (change.type !== ProjectChangeType.ElementRecipeApply) return latest;
  if (change.recipeId !== recipeId && change.recipe?.id !== recipeId) {
    return latest;
  }
  if (!isRecipe(change.recipe)) return latest;
  return change.recipe;
};

/**
 * Latest frozen {@link Recipe} payload from proposal actions for {@link recipeId}.
 * Iterates {@link IProposal.actionIds} order (not {@link Baseline.actions} array order).
 */
const getEmbeddedRecipeFromProjectActions = (
  project: Project,
  recipeId: RecipeID,
): Recipe | undefined => {
  let latest: Recipe | undefined;

  for (const version of getAllBuildingVersions(project)) {
    const actions = version.actions;
    if (!actions?.length) continue;

    const proposalReferencedActionIds = new Set<string>();

    for (const proposal of version.proposals ?? []) {
      for (const actionId of proposal.actionIds) {
        proposalReferencedActionIds.add(actionId);
      }
      for (const change of collectProposalChangesInOrder(version, proposal)) {
        latest = updateLatestEmbeddedRecipeFromChange(change, recipeId, latest);
      }
    }

    // Fallback for actions not linked from any proposal (legacy fixtures / invalid graphs).
    for (const action of actions) {
      if (proposalReferencedActionIds.has(action.id)) continue;
      for (const change of action.changes) {
        latest = updateLatestEmbeddedRecipeFromChange(change, recipeId, latest);
      }
    }
  }

  return latest;
};

/**
 * Resolves referenced recipe ids against a lookup, falling back to action payloads.
 */
const resolveRecipesForReferencedIds = (
  project: Project,
  availableRecipes: RecipeRecord,
): { recipes: Recipe[]; missingRecipeIds: RecipeID[] } => {
  const recipeIds = getRecipeIdsReferencedByProject(project, true);
  const recipes: Recipe[] = [];
  const missingRecipeIds: RecipeID[] = [];
  const seen = new Set<RecipeID>();

  for (const id of recipeIds) {
    if (seen.has(id)) continue;

    const fromLookup = availableRecipes[id];
    if (isRecipe(fromLookup)) {
      recipes.push(fromLookup);
      seen.add(id);
      continue;
    }

    const fromAction = getEmbeddedRecipeFromProjectActions(project, id);
    if (isRecipe(fromAction)) {
      recipes.push(fromAction);
      seen.add(fromAction.id);
      if (fromAction.id !== id) seen.add(id);
      continue;
    }

    missingRecipeIds.push(id);
  }

  return { recipes, missingRecipeIds };
};

/**
 * Recipes referenced on {@link project} (tree + proposal actions), resolved from
 * {@link recipes} and frozen action payloads when not in the lookup.
 */
export const getRecipesUsedInProject = (
  project: Project,
  recipes: Recipe[] | RecipeRecord,
): Recipe[] => {
  const lookup = Array.isArray(recipes) ? toLookup(recipes) : recipes;
  return resolveRecipesForReferencedIds(project, lookup).recipes;
};

/**
 * Resolves every referenced recipe for export bundling; reports ids with no lookup or action payload.
 */
export const collectRecipesForProjectExport = (
  project: Project,
  availableRecipes: RecipeRecord,
): { recipes: Recipe[]; missingRecipeIds: RecipeID[] } =>
  resolveRecipesForReferencedIds(project, availableRecipes);

/**
 * @throws {Error} When one or more referenced recipe ids could not be resolved for export
 */
export const throwIfMissingReferencedRecipesForExport = (
  missingRecipeIds: RecipeID[],
): void => {
  if (missingRecipeIds.length === 0) return;

  throw new Error(
    `Cannot export project: ${missingRecipeIds.length} recipe(s) referenced by the project are missing from the recipe library. Reload the page or open the recipe library, then try again. Missing recipe id(s): ${missingRecipeIds.join(', ')}`,
  );
};

/**
 * Ensures every recipe referenced by {@link project} is present in {@link availableRecipes}.
 * @throws {Error} When one or more referenced recipes are missing from the library
 */
export const validateAllReferencedRecipesAvailableForExport = (
  project: Project,
  availableRecipes: RecipeRecord,
): void => {
  const { missingRecipeIds } = collectRecipesForProjectExport(
    project,
    availableRecipes,
  );
  throwIfMissingReferencedRecipesForExport(missingRecipeIds);
};

/**
 * Test if id is an id that comes from a recipe in the DB (not hardcoded or auto etc)
 * @param id
 * @returns
 */
const isValidStoreRecipeId = (
  id: RecipeID | undefined,
  allowAutoRecipe = false,
): id is RecipeID =>
  !!id &&
  (allowAutoRecipe || !isAutoRecipeId(id)) &&
  id !== 'none' &&
  id !== 'detach';

/**
 * Get recipe id from an element or property.
 * Note that it will be the raw id (like 'auto', 'auto:123jlhq54' or 'none') and not the resolved id
 * @param elementOrProperty
 * @returns
 */
export const getRecipeId = (
  elementOrProperty:
    | OneOfElements
    | OneOfFactoryElements
    | IElementProperty
    | RecipeID
    | undefined,
): RecipeID | undefined => {
  if (isPrimitive(elementOrProperty)) {
    return elementOrProperty;
  }
  return ElementPropertySource.Recipe in elementOrProperty
    ? elementOrProperty.recipe_id
    : undefined;
};

/**
 * Get the name of a recipe from an element or factory element
 * @param element
 * @param recipeLookup
 * @returns
 */
export const getRecipeName = (
  element: OneOfElements | OneOfFactoryElements,
  recipeLookup?: RecipeRecord,
): string | undefined => {
  const recipeId = getRecipeId(element);

  if (!recipeId || !isElement(element)) {
    return undefined;
  }
  return recipeLookup ? recipeLookup[recipeId]?.name : element.fallbackName;
};

/**
 * Get all product ids that exist within a recipe or list of recipes
 * @param recipes
 * @returns
 */
export const getProductIdsInRecipes = (...recipes: Recipe[]): ProductID[] => {
  const ingredients = recipes.flatMap((r) => r.elements);
  return uniq(ingredients.flatMap((el) => getProductIdsInElement(el)));
};

/**
 * Get all recipes that can be applied to an element (keep order by co2)
 * @param sortedRecipes
 * @param categoryID
 * @param groupUnmappedRecipesInOther If true all recipes that can't be mapped to a enabled category will be grouped in the "Other" category
 * @returns
 */
export const getApplicableRecipes = (
  sortedRecipes: Recipe[],
  element: OneOfElements | undefined,
  groupUnmappedRecipesInOther = false,
): Recipe[] => {
  if (!isElement(element)) {
    return EMPTY_FROZEN_ARRAY as Recipe[];
  }
  return sortedRecipes.filter((recipe) =>
    isApplicableRecipe(element, recipe, groupUnmappedRecipesInOther),
  );
};

const isApplicableRecipe = (
  element: IElement,
  recipe: Recipe,
  groupUnmappedRecipesInOther = false,
): boolean => {
  const category = getElementCategory(element);
  const filterFn = category?.recipeFilter;
  if (recipeHasCategoryId(recipe, category?.id, groupUnmappedRecipesInOther)) {
    return filterFn ? filterFn(recipe, element) : true;
  }
  return false;
};

/**
 * Get the recipe to use based on ID.
 * If auto id return first applicable recipe (which has lowest co2e).
 * If regular id return the recipe with that id if it is applicable else undefined.
 * @param sortedRecipes
 * @param element
 * @param id Pass an id to override the id on the element
 * @returns
 */
export const getApplicableRecipe = (
  sortedRecipes: Recipe[],
  element: IElement,
  id: RecipeID | undefined = element.recipe_id,
  groupUnmappedRecipesInOther = false,
): Recipe | undefined => {
  if (id) {
    const applicableRecipes = getApplicableRecipes(
      sortedRecipes,
      element,
      groupUnmappedRecipesInOther,
    );

    // Always return the first recipe if id is any auto value
    if (isAutoRecipeId(id)) {
      return applicableRecipes[0];
    }
    return applicableRecipes.find((recipe) => recipe.id === id);
  }
};

/**
 * Test if recipe belongs to the given category_id
 * @param recipe
 * @param id Id of the category
 * @param groupUnmappedRecipesInOther If true all recipes that can't be mapped to a enabled category will be grouped in the "Other" category
 * @returns
 */
const recipeHasCategoryId = (
  recipe: Recipe,
  id: ElementCategoryID | undefined,
  groupUnmappedRecipesInOther = false,
): boolean => {
  if (recipe.category_id === id) {
    return true;
  }

  // Other category should pick up any recipes that are not in any other visible category
  if (
    groupUnmappedRecipesInOther &&
    recipe.category_id &&
    id === ElementCategoryID.Other
  ) {
    const categoryIds = getAvailableCategories().map((category) => category.id);
    return !categoryIds.includes(recipe.category_id);
  }
  return false;
};

/**
 * Check if id is an auto id: "auto" or "auto:recipeId"
 * @param element
 * @returns
 */
export const isAutoRecipeId = (element?: OneOfElements | RecipeID): boolean => {
  const id = getRecipeId(element);
  return id === 'auto' || id?.indexOf(AUTO_RECIPE_ID_PREFIX) === 0;
};

/**
 * Get which auto element that is currently in use.
 * Older recipes might have "auto" as id and then we don't know which one to use
 * @param elementOrId
 * @returns If possible to determine which id is used, return that. Otherwise undefined
 */
export const resolveAutoRecipeId = (
  elementOrId?: OneOfElements | RecipeID,
): RecipeID | undefined => {
  const id = getRecipeId(elementOrId);
  if (id && isAutoRecipeId(id)) {
    return id === 'auto' || id === AUTO_RECIPE_ID_PREFIX
      ? undefined
      : id.substring(AUTO_RECIPE_ID_PREFIX.length);
  }
  return id;
};

/**
 * Whether a recipe is soft-deleted. Soft-deleted recipes are kept in the store so
 * element/property references still resolve, but are hidden from pickers/library.
 */
export const isDeletedRecipe = (recipe: Pick<Recipe, 'deleted_at'>): boolean =>
  Boolean(recipe.deleted_at);

/** Recipes visible in pickers/library (excludes soft-deleted). */
export const getVisibleRecipes = <T extends Pick<Recipe, 'deleted_at'>>(
  recipes: readonly T[],
): T[] => recipes.filter((recipe) => !isDeletedRecipe(recipe));

/**
 * Recipe names that count as taken when suggesting or validating a new recipe name.
 * Soft-deleted recipes are excluded so a deleted name can be reused.
 */
export const getVisibleRecipeNames = (
  recipes: readonly Pick<Recipe, 'name' | 'deleted_at'>[],
): string[] => getVisibleRecipes(recipes).map((recipe) => recipe.name);

/**
 * Check if an unkonwn type is a recipe
 * @param element
 * @returns
 */
export const isRecipe = (element: unknown): element is Recipe => {
  return (
    isObject(element) &&
    hasDefinedProperties(
      element as Recipe,
      'id',
      'name',
      'category_id',
      'category_property_value_record',
    )
  );
};

export const isNotInSortedRecipes = (
  recipeId: string | undefined,
  sortedRecipes: Recipe[],
): boolean =>
  !isAutoRecipeId(recipeId) &&
  !sortedRecipes.some((recipe) => recipe.id === recipeId);

/**
 * Compare if two category property value records are equal
 * @param prev
 * @param next
 * @param ignoreKeys List of keys to ignore when comparing
 * @returns
 */
export const isEqualCategoryPropertyValueRecord = (
  prev: PropertyResolvedCountRecord,
  next: PropertyResolvedCountRecord,
  ignoreKeys: (keyof PropertyResolvedCountRecord)[] = [
    ElementPropertyName.Lifetime,
    ElementPropertyName.SbefCode,
    ElementPropertyName.ContentCoverage,
    ...([
      ...REMOVED_CATEGORY_PROPERTY_NAMES,
    ] as (keyof PropertyResolvedCountRecord)[]),
  ],
): boolean => {
  const filterRecord = (record: PropertyResolvedCountRecord) =>
    mapFilterRecord(omit(record, ...ignoreKeys), (value) => {
      if (['number', 'undefined'].includes(typeof value)) {
        return undefined;
      }
      return value;
    });
  return shallowEqual(filterRecord(prev), filterRecord(next));
};
